import { _ } from "libs";

import React, { useState, useRef, useEffect } from "react";
import PartnersInput from "./partnersInput";
import MessageEditor from "./messageEditor";
import { User, Profile } from "../../types";
import ChatSocketContextContext from "../../contexts/chatSocketContext";
import ChatSocketManager from "../../libs/chatSocketManager";
import Messages from "./messages";
import ConversationsMap from "../../libs/conversationsMap";
import TypingIndicator from "./typingIndicator";
import { getDefaultMessage } from "./defaultMessages";

type Props = {
	onCreateConversation: (conversationId: string) => void;
	chatSocket?: ChatSocketManager;
	preload?: string[];
	userId: string;
	defaultMessageType?: string;
	profile: Profile;
	messageParams?: object;
};

const ConversationComposer = ({
	onCreateConversation,
	preload = [],
	userId,
	chatSocket,
	defaultMessageType = "",
	profile,
	messageParams = {},
}: Props) => {
	const [partners, setPartners] = useState([] as User[]);
	const [conversation, setConversation] = useState(null);
	const input = useRef<typeof PartnersInput>(null as typeof PartnersInput);
	const editor = useRef<typeof MessageEditor>(null as typeof MessageEditor);

	useEffect(() => {
		input.current.focus();
	}, []);

	const getUserIds = (partners: User[]): string[] => {
		return partners.map((partner: User) => partner.userId);
	};

	const onPartnersChange = async (partners: User[]) => {
		setPartners(partners);
		const userIds = getUserIds(partners);

		const res = (await chatSocket.getConversation(userIds)) as any;
		if (res.payload) {
			const conversation = ConversationsMap.convertConversation(res.payload);
			setConversation(conversation);
		} else {
			setConversation(null);
		}
	};

	const createConversation = async (message: string) => {
		const userIds = getUserIds(partners);
		const res = (await chatSocket.createConversation(message, userIds)) as any;
		onCreateConversation(res.payload.conversationId);
	};

	return (
		<div className="conversation newConversation">
			<div className="conversationHeader">
				<div className="left">
					<h5>{_("chat.composer.newConversation")}</h5>
				</div>
				<div className="title">&nbsp;</div>
			</div>
			<div className="conversationBody">
				<div className="partnersInput">
					<PartnersInput onChange={onPartnersChange} preload={preload} input={input} />
				</div>
				{conversation ? (
					<>
						<Messages conversation={conversation} userId={userId} />
						<TypingIndicator partners={null} myId={userId} />
					</>
				) : null}
			</div>
			<div className="conversationFooter">
				{partners.length > 0 ? (
					<MessageEditor
						onSend={createConversation}
						ref={editor}
						defaultMessage={getDefaultMessage(defaultMessageType, {
							firstNameChallengee: partners[0].firstName,
							firstNameChallenger: profile.firstName,
							...messageParams,
						})}
					/>
				) : null}
			</div>
		</div>
	);
};

const ConversationComposerWithContext = (props: Props) => (
	<ChatSocketContextContext.Consumer>
		{context => <ConversationComposer {...props} chatSocket={context} />}
	</ChatSocketContextContext.Consumer>
);

export default ConversationComposerWithContext;
